/**
 * Shared helpers for turning axios/backend error responses into safe,
 * human-readable strings for React rendering and toasts.
 *
 * FastAPI + Pydantic v2 can return `detail` as:
 *   • a plain string ("Course not found")
 *   • an array of ValidationError dicts
 *       [{ type, loc, msg, input, url }, ...]
 *   • an object dict for custom exceptions
 *
 * Rendering the raw array/dict into JSX blows up with
 *   "Objects are not valid as a React child (found: object with
 *    keys {type, loc, msg, input, url})".
 *
 * Always route error payloads through `stringifyError()` before you
 * hand them to toast/state/JSX.
 */

// Format one Pydantic ValidationError item into "path: msg".
function formatValidationItem(item) {
  if (!item || typeof item !== 'object') return String(item);
  const loc = Array.isArray(item.loc) ? item.loc.filter((p) => p !== 'body').join('.') : '';
  const msg = item.msg || item.message || '';
  if (loc && msg) return `${loc}: ${msg}`;
  if (msg) return String(msg);
  try { return JSON.stringify(item); } catch { return String(item); }
}

// Extract a safe error string from anything we might catch — axios
// response, thrown Error, plain string, or nothing.
export function stringifyError(err, fallback = 'Something went wrong.') {
  if (!err) return fallback;
  if (typeof err === 'string') return err;

  // Axios error shape.
  const detail = err?.response?.data?.detail;
  if (typeof detail === 'string' && detail.trim()) return detail;
  if (Array.isArray(detail)) return detail.map(formatValidationItem).join('; ');
  if (detail && typeof detail === 'object') return formatValidationItem(detail);

  // Fallback to Error.message / plain message / stringified body.
  if (typeof err.message === 'string' && err.message.trim()) return err.message;
  const raw = err?.response?.data;
  if (typeof raw === 'string' && raw.trim()) return raw;
  if (raw && typeof raw === 'object') {
    try { return JSON.stringify(raw); } catch { /* fallthrough */ }
  }
  return fallback;
}
